import { useState, type ReactElement } from 'react';

import { useMe } from '@/hooks/useMe';
import { TaskEditDialog } from '@/features/patient-record/components/panels/TasksPanel/TaskEditDialog';

import { type TaskInboxStatus } from '../queries';
import { DEFAULT_TASK_SORT_ID, isGroupOnlySort } from '../lib/taskInboxSort';
import { TaskInboxControls, type TaskInboxView } from './TaskInboxControls';
import { TaskInboxList } from './TaskInboxList';
import { TaskInboxGrouped } from './TaskInboxGrouped';
import type { OnEditTask } from './TaskInboxRowItem';

interface EditTarget {
  readonly guid: string;
  readonly taskId: string;
}

/**
 * Cross-patient task inbox (PTV2-1236). Lists the signed-in user's tasks via
 * `GET /persons/tasks`, defaulting to their own open (todo) tasks. PTV2-1321
 * adds server-side sorting (due date / priority) to the paginated list and a
 * "by patient" grouped view. Rows reuse the patient-record task components so
 * completing / editing a task here behaves exactly as it does on a record.
 */
export function MyCasesInbox(): ReactElement {
  const { data: me } = useMe();
  const assignee = me?.id ?? '';

  const [status, setStatus] = useState<TaskInboxStatus>('todo');
  const [view, setView] = useState<TaskInboxView>('list');
  const [sortId, setSortId] = useState<string>(DEFAULT_TASK_SORT_ID);
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);

  const onEdit: OnEditTask = (guid, taskId) => {
    setEditTarget({ guid, taskId });
  };

  const onViewChange = (next: TaskInboxView): void => {
    setView(next);
    // The task-count sort only exists while grouping — fall back when leaving it.
    if (next === 'list' && isGroupOnlySort(sortId)) setSortId(DEFAULT_TASK_SORT_ID);
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 p-6">
      <TaskInboxControls
        status={status}
        onStatusChange={setStatus}
        view={view}
        onViewChange={onViewChange}
        sortId={sortId}
        onSortChange={setSortId}
      />

      {view === 'list' ? (
        <TaskInboxList assignee={assignee} status={status} sortId={sortId} onEdit={onEdit} />
      ) : (
        <TaskInboxGrouped assignee={assignee} status={status} sortId={sortId} onEdit={onEdit} />
      )}

      {editTarget !== null && (
        <TaskEditDialog
          guid={editTarget.guid}
          taskId={editTarget.taskId}
          onClose={() => {
            setEditTarget(null);
          }}
        />
      )}
    </div>
  );
}
